import { TOUCHPOINT_CONFIGS, type TouchpointId, type TouchpointConfig } from '../types/touchpoints';
import type { DailyCheckIn } from '../types/localStorage';
import type { Persona } from '../types/survey';
import type { MetaType, RichPersonaProfile } from '../types/richPersona';
import { getMetaTypeById } from '../data/metaTypes';
import { buildUserContext, buildSmartNudgeMessage } from './userContext';

// ─────────────────────────────────────────────────
// Nudge Service — Smart touchpoint recommendations
// Determines what the user should do based on time + state
// ─────────────────────────────────────────────────

export interface NudgeRecommendation {
  touchpoint: TouchpointConfig;
  message: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
}

/** Get current hour (0-23) */
function getCurrentHour(): number {
  return new Date().getHours();
}

/** Get today's date string YYYY-MM-DD */
function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

/** Check if today is Sunday (for weekly synthesis) */
function isSunday(): boolean {
  return new Date().getDay() === 0;
}

/** Get touchpoints available for the current time */
export function getActiveTouchpoints(): TouchpointConfig[] {
  const hour = getCurrentHour();
  return TOUCHPOINT_CONFIGS.filter(tp => {
    if (tp.id === 'weekly_synthesis') return isSunday();
    return hour >= tp.timeRange.start && hour < tp.timeRange.end;
  });
}

/** Check which touchpoints have been completed today */
export function getCompletedTouchpoints(
  checkIns: DailyCheckIn[],
  timerSessions: Array<{ startTime?: string; date?: string }>,
  pulseRecords: Array<{ date: string }>,
  growthMoments: Array<{ date: string }>,
): Set<TouchpointId> {
  const today = getToday();
  const completed = new Set<TouchpointId>();

  // Morning/evening check-ins
  const todayCheckIns = checkIns.filter(c => c.date === today);
  if (todayCheckIns.some(c => c.type === 'morning')) completed.add('morning_nudge');
  if (todayCheckIns.some(c => c.type === 'evening')) completed.add('evening_reflection');

  // Timer sessions
  const todayTimers = timerSessions.filter(s => {
    const d = s.date || (s.startTime ? s.startTime.split('T')[0] : '');
    return d === today;
  });
  if (todayTimers.length > 0) {
    const hour = getCurrentHour();
    if (hour < 13) completed.add('focus_session');
    else completed.add('afternoon_focus');
  }

  // Pulse
  if (pulseRecords.some(p => p.date === today)) completed.add('midday_pulse');

  // Growth moment
  if (growthMoments.some(g => g.date === today)) completed.add('growth_moment');

  return completed;
}

/** Get personalized nudge message based on MetaType */
function getPersonalizedMessage(
  touchpoint: TouchpointConfig,
  metaType: MetaType | null,
  persona: Persona,
): string {
  if (metaType) {
    const routine = metaType.dailyRoutine;
    switch (touchpoint.id) {
      case 'morning_nudge': return `${metaType.emoji} ${routine.morning}`;
      case 'focus_session': return `${metaType.emoji} ${routine.focus}`;
      case 'midday_pulse': return `${metaType.emoji} ${routine.lunch}`;
      case 'afternoon_focus': return `${metaType.emoji} 오후에도 힘내요! ${routine.focus}`;
      case 'evening_reflection': return `${metaType.emoji} ${routine.evening}`;
      case 'growth_moment': return `${metaType.emoji} ${routine.growth}`;
      case 'weekly_synthesis': return `${metaType.emoji} 한 주를 돌아보는 시간이에요!`;
    }
  }

  // Fallback persona-based messages
  const defaults: Record<Persona, Record<string, string>> = {
    elementary: {
      morning_nudge: '🌅 좋은 아침! 오늘 뭘 해볼까?',
      focus_session: '⏱️ 집중 시간이야! 같이 해보자!',
      midday_pulse: '⚡ 지금 기분은 어때?',
      afternoon_focus: '🎯 오후에도 힘내자!',
      evening_reflection: '🌙 오늘 하루 어땠어?',
      growth_moment: '💡 오늘 배운 것을 떠올려봐요!',
      weekly_synthesis: '📊 이번 주 잘했는지 확인해볼까?',
    },
    secondary: {
      morning_nudge: '🌅 좋은 아침! 오늘의 계획을 세워볼까?',
      focus_session: '⏱️ 집중 모드 ON! 시작해보자.',
      midday_pulse: '⚡ 에너지 체크! 지금 컨디션은?',
      afternoon_focus: '🎯 오후 집중 시간!',
      evening_reflection: '🌙 오늘 하루를 돌아보자.',
      growth_moment: '💡 오늘 한 가지 생각해볼 것.',
      weekly_synthesis: '📊 이번 주 리포트를 확인해볼까?',
    },
    university: {
      morning_nudge: '🌅 오늘의 목표를 확인해볼까요?',
      focus_session: '⏱️ 딥 포커스 세션을 시작할 시간입니다.',
      midday_pulse: '⚡ 중간 점검 — 진행 상황은 어떠세요?',
      afternoon_focus: '🎯 오후 집중 시간입니다.',
      evening_reflection: '🌙 하루를 돌아보며 정리할 시간이에요.',
      growth_moment: '💡 오늘의 성찰 질문이 준비되어 있어요.',
      weekly_synthesis: '📊 주간 분석 리포트를 확인해보세요.',
    },
    professional: {
      morning_nudge: '🌅 오늘의 핵심 과제를 점검해보시겠습니까?',
      focus_session: '⏱️ 딥 워크 세션을 시작할 시간입니다.',
      midday_pulse: '⚡ 진행 상황 중간 점검.',
      afternoon_focus: '🎯 오후 딥 워크 시간입니다.',
      evening_reflection: '🌙 하루를 돌아보며 정리해보시겠습니까.',
      growth_moment: '💡 오늘의 성장 질문이 있습니다.',
      weekly_synthesis: '📊 주간 성과 리포트를 확인하세요.',
    },
  };

  return defaults[persona][touchpoint.id] || touchpoint.description;
}

// ─── Weekly Cycle Nudges ─────────────────────────
// Day-of-week nudge messages for weekly routine cycle
// Monday: goal review, Wednesday: midweek check, Friday: weekly reflection

type WeekdayId = 'monday' | 'wednesday' | 'friday';

export interface WeeklyCycleNudge {
  dayOfWeek: WeekdayId;
  message: string;
  link: string;
  label: string;
}

const WEEKLY_CYCLE_MESSAGES: Record<WeekdayId, Record<Persona, string>> = {
  monday: {
    elementary: '새로운 한 주가 시작됐어! 🌟 이번 주 목표를 확인해볼까?',
    secondary: '월요일이야! 이번 주 목표를 확인하고 계획을 세워보자.',
    university: '새로운 한 주가 시작되었어요. 이번 주 목표를 확인해볼까요?',
    professional: '새로운 한 주입니다. 이번 주 핵심 목표를 점검해보세요.',
  },
  wednesday: {
    elementary: '벌써 수요일! 이번 주 어떻게 하고 있는지 중간점검 해볼까? 📋',
    secondary: '수요일 중간점검! 이번 주 진행 상황을 확인해보자.',
    university: '한 주의 중간, 진행 상황을 점검해볼까요?',
    professional: '한 주의 중간입니다. 진행 상황을 점검해보세요.',
  },
  friday: {
    elementary: '금요일이야! 🎉 이번 주 어떤 한 주였는지 같이 돌아보자.',
    secondary: '금요일! 한 주를 돌아보면 다음 주가 더 좋아져. 같이 정리해볼까?',
    university: '한 주를 마무리하며 돌아보는 시간이에요. 성장을 확인해보세요.',
    professional: '한 주를 마무리합니다. 이번 주 성과를 돌아보고 다음 주를 준비해보세요.',
  },
};

const WEEKLY_CYCLE_LINKS: Record<WeekdayId, { link: string; label: string }> = {
  monday: { link: '/goals', label: '목표 확인하기' },
  wednesday: { link: '/checkin', label: '중간점검 하기' },
  friday: { link: '/profile/weekly-report', label: '주간 리포트 보기' },
};

/** Map JS Date.getDay() → WeekdayId (only Mon/Wed/Fri) */
function getTodayWeekdayId(): WeekdayId | null {
  const day = new Date().getDay();
  if (day === 1) return 'monday';
  if (day === 3) return 'wednesday';
  if (day === 5) return 'friday';
  return null;
}

/**
 * Returns a weekly cycle nudge if today is Monday, Wednesday, or Friday.
 * Messages are persona-aware.
 */
export function getWeeklyCycleNudge(persona: Persona): WeeklyCycleNudge | null {
  const weekdayId = getTodayWeekdayId();
  if (!weekdayId) return null;

  const { link, label } = WEEKLY_CYCLE_LINKS[weekdayId];
  return {
    dayOfWeek: weekdayId,
    message: WEEKLY_CYCLE_MESSAGES[weekdayId][persona],
    link,
    label,
  };
}

/** Get the recommended next touchpoint with personalized message */
export function getNextRecommendation(
  checkIns: DailyCheckIn[],
  timerSessions: Array<{ startTime?: string; date?: string }>,
  pulseRecords: Array<{ date: string }>,
  growthMoments: Array<{ date: string }>,
  persona: Persona,
  richProfile?: RichPersonaProfile | null,
): NudgeRecommendation | null {
  const activeTouchpoints = getActiveTouchpoints();
  const completed = getCompletedTouchpoints(checkIns, timerSessions, pulseRecords, growthMoments);

  const metaType = richProfile?.metaTypeId
    ? getMetaTypeById(richProfile.metaTypeId)
    : null;

  // Filter to uncompleted active touchpoints
  const pending = activeTouchpoints.filter(tp => !completed.has(tp.id));

  if (pending.length === 0) return null;

  // Prioritize by order (morning first, etc.)
  const touchpoint = pending[0];

  // Try smart nudge (If-Then, pattern-based, goal-aware)
  const userCtx = buildUserContext(persona);
  const smartMessage = buildSmartNudgeMessage(touchpoint.id, userCtx);

  // Determine reason
  let reason = '현재 시간대에 맞는 활동이에요.';
  if (smartMessage) {
    if (userCtx.ifThenGoals.length > 0) reason = '실행 계획에 맞는 시간이에요!';
    else if (userCtx.energy.lowEnergyDay) reason = `${userCtx.energy.todayDayName}요일 에너지 패턴 기반 추천`;
    else reason = '오늘 활동 데이터 기반 맞춤 추천';
  }

  return {
    touchpoint,
    message: smartMessage || getPersonalizedMessage(touchpoint, metaType, persona),
    priority: smartMessage ? 'high' : (pending.length <= 2 ? 'high' : 'medium'),
    reason,
  };
}
